import {
    DateTime,
    Duration,
} from 'luxon'
import {
    defaultTicketSlaReminderTime,
    defaultTicketSlaWarningTime,
    parseSlaDurationSeconds,
} from 'utils/queueSettings'
import {
    useEffect,
    useRef,
    useState,
} from 'react'
import {timerCacheService} from 'utils/cache.service'

type UseQueueingTimeOptions = {
    deliveryTime?: number
    displayFormat?: QueueingTimeDisplayFormat
    endTime?: number
    queueLength: number
    queueTime: number
    timerKey?: string
}

type UseQueueingTimerOptions = UseQueueingTimeOptions & {
    sla?: boolean
    slaReminderTime?: string
    slaWarningTime?: string
}

type UseSlaStateOptions = {
    enabled?: boolean
    endTime?: number
    queueLength: number
    queueTime: number
    reminderTime?: string
    warningTime?: string
}

export type QueueingTimeDisplayFormat = 'coarse' | 'precise'
export type SlaState = 'normal' | 'reminder' | 'warning'

type FormatCounterDurationOptions = {
    displayFormat?: QueueingTimeDisplayFormat
    endTime?: number
    now?: DateTime
}

export type QueueingTimeValue = {
    dateTime?: string
    time?: string
}

export type QueueingTimerValue = QueueingTimeValue & {
    slaState?: SlaState
}

const getElapsedQueueDuration = (
    queueTimeSeconds?: number,
    nowOrOptions: DateTime | Pick<FormatCounterDurationOptions, 'endTime' | 'now'> = DateTime.now(),
    endTimeOverride?: number,
): Duration => {
    const options = DateTime.isDateTime(nowOrOptions) ? undefined : nowOrOptions
    const now = DateTime.isDateTime(nowOrOptions) ? nowOrOptions : options?.now ?? DateTime.now()
    const endTime = endTimeOverride ?? options?.endTime
    const queueStartTime = queueTimeSeconds ? DateTime.fromSeconds(queueTimeSeconds) : now
    const queueEndTime = endTime ? DateTime.fromSeconds(endTime) : now

    return Duration.fromObject({seconds: Math.max(0, Math.floor(queueEndTime.diff(queueStartTime).as('seconds')))})
}

const formatDurationUnit = (elapsedDuration: Duration): string => {
    if (elapsedDuration.as('hours') < 1) {
        return `${Math.floor(elapsedDuration.as('minutes'))}min`
    }

    if (elapsedDuration.as('days') < 1) {
        return `${Math.floor(elapsedDuration.as('hours'))}h`
    }

    return `${Math.floor(elapsedDuration.as('days'))}d`
}

const formatElapsedDuration = (
    elapsedDuration: Duration,
    displayFormat: QueueingTimeDisplayFormat,
): string => {
    if (displayFormat === 'coarse') {
        return formatDurationUnit(elapsedDuration)
    }

    if (elapsedDuration.as('minutes') < 3) {
        return elapsedDuration.shiftTo('minutes', 'seconds').toFormat('m:ss')
    }

    return formatDurationUnit(elapsedDuration)
}

const formatElapsedDurationDateTime = (elapsedDuration: Duration): string => elapsedDuration
    .shiftTo('days', 'hours', 'minutes', 'seconds')
    .toISO() ?? 'PT0S'

const formatCounterDuration = (
    queueTimeSeconds?: number,
    nowOrOptions: DateTime | FormatCounterDurationOptions = DateTime.now(),
    endTimeOverride?: number,
): string => {
    const options = DateTime.isDateTime(nowOrOptions) ? undefined : nowOrOptions
    const displayFormat = options?.displayFormat ?? 'precise'
    const elapsedDuration = getElapsedQueueDuration(queueTimeSeconds, nowOrOptions, endTimeOverride)

    return formatElapsedDuration(elapsedDuration, displayFormat)
}

export const formatQueueDuration = formatCounterDuration
export const formatTicketDuration = formatCounterDuration // may be use later if format diverse

export const getSlaState = ({
    enabled,
    endTime,
    queueLength,
    queueTime,
    reminderTime,
    warningTime,
}: UseSlaStateOptions): SlaState | undefined => {
    if (!enabled || queueLength === 0 || queueTime === 0) {
        return undefined
    }

    const queueEndTime = endTime ? DateTime.fromSeconds(endTime) : DateTime.now()
    const elapsedSeconds = Math.max(0, queueEndTime.diff(DateTime.fromSeconds(queueTime), 'seconds')
        .as('seconds'))
    const reminderSeconds = parseSlaDurationSeconds(reminderTime, defaultTicketSlaReminderTime)
    const warningSeconds = parseSlaDurationSeconds(warningTime, defaultTicketSlaWarningTime)

    if (warningSeconds !== undefined && elapsedSeconds >= warningSeconds) {
        return 'warning'
    }

    if (reminderSeconds !== undefined && elapsedSeconds >= reminderSeconds) {
        return 'reminder'
    }

    return 'normal'
}

export const useQueueingTimer = ({
    deliveryTime,
    displayFormat = 'precise',
    endTime,
    queueLength,
    queueTime,
    sla,
    slaReminderTime,
    slaWarningTime,
    timerKey,
}: UseQueueingTimerOptions): QueueingTimerValue => {
    const [queueingTimer, setQueueingTimer] = useState<QueueingTimerValue>({})
    const [currentQueueTime, setCurrentQueueTime] = useState<number>(0)

    const hasInitializedRef = useRef(false)
    const previousLengthRef = useRef(0)
    const previousTimerKeyRef = useRef(timerKey)

    const clearTimerCache = (onlyPrevious = false) => {
        timerCacheService.clear(previousTimerKeyRef.current)
        if (onlyPrevious) {
            return
        }
        timerCacheService.clear(timerKey)
    }

    useEffect(() => {
        const timerKeyChanged = previousTimerKeyRef.current !== timerKey
        const isEmptyToFirstCustomer = hasInitializedRef.current &&
            timerKeyChanged &&
            previousLengthRef.current === 0 &&
            queueLength > 0
        if (hasInitializedRef.current && timerKeyChanged) {
            clearTimerCache(true)
        }

        if (!hasInitializedRef.current || timerKeyChanged) {
            const fallbackQueueTime = isEmptyToFirstCustomer ? deliveryTime ?? queueTime : queueTime
            const nextQueueTime = queueLength > 0 ? timerCacheService.get(timerKey, fallbackQueueTime) : 0

            if (queueLength === 0) {
                clearTimerCache()
            } else {
                timerCacheService.set(timerKey, nextQueueTime)
            }
            setCurrentQueueTime(nextQueueTime)
            previousLengthRef.current = queueLength
            previousTimerKeyRef.current = timerKey
            hasInitializedRef.current = true
            return
        }

        // When queue goes from empty to having customers, use delivery time if available.
        // When queue length decreases, reset to the current queue time.
        // https://github.com/somia/ninchat-app/pull/1629
        if (queueLength === 0) {
            clearTimerCache()
            setCurrentQueueTime(0)
        } else if (queueLength > previousLengthRef.current && previousLengthRef.current === 0) {
            const nextQueueTime = deliveryTime ?? queueTime

            timerCacheService.set(timerKey, nextQueueTime)
            setCurrentQueueTime(nextQueueTime)
        } else if (queueLength < previousLengthRef.current) {
            timerCacheService.set(timerKey, queueTime)
            setCurrentQueueTime(queueTime)
        }
        previousLengthRef.current = queueLength
    }, [
        deliveryTime,
        queueLength,
        queueTime,
        timerKey,
    ])

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null

        const updateQueueTime = () => {
            const elapsedDuration = getElapsedQueueDuration(currentQueueTime, {endTime})

            setQueueingTimer({
                dateTime: formatElapsedDurationDateTime(elapsedDuration),
                slaState: getSlaState({
                    enabled: sla,
                    endTime,
                    queueLength,
                    queueTime: currentQueueTime,
                    reminderTime: slaReminderTime,
                    warningTime: slaWarningTime,
                }),
                time: formatElapsedDuration(elapsedDuration, displayFormat),
            })

            if (endTime === undefined) {
                const currentTimeMilliseconds = DateTime.now().toMillis()
                const currentTimeSeconds = currentTimeMilliseconds / 1000
                const elapsedSeconds = Math.max(0, currentTimeSeconds - currentQueueTime)
                const nextElapsedSecond = currentQueueTime + Math.floor(elapsedSeconds) + 1
                const nextElapsedMilliseconds = nextElapsedSecond * 1000
                const millisecondsUntilNextSecond = Math.max(
                    1,
                    Math.ceil(nextElapsedMilliseconds - currentTimeMilliseconds),
                )

                timer = setTimeout(updateQueueTime, millisecondsUntilNextSecond)
            }
        }

        updateQueueTime()
        return () => {
            if (timer !== null) {
                clearTimeout(timer)
            }
        }
    }, [
        currentQueueTime,
        displayFormat,
        endTime,
        queueLength,
        sla,
        slaReminderTime,
        slaWarningTime,
    ])

    // eslint-disable-next-line max-lines
    if (queueLength === 0 || currentQueueTime === 0) {
        return {}
    }

    return queueingTimer
}

export const useQueueingTimeValue = (
    options: UseQueueingTimeOptions,
): QueueingTimeValue => {
    const {
        dateTime,
        time,
    } = useQueueingTimer(options)

    return {
        dateTime,
        time,
    }
}

export const useQueueingTime = (
    options: UseQueueingTimeOptions,
): string | undefined => useQueueingTimeValue(options).time
